'use client';

import React from 'react';
import clsx from 'clsx';

export interface InfoCardProps extends React.HTMLAttributes<HTMLDivElement> {
  /**
   * Card title displayed in the header
   */
  title: string;

  /**
   * Optional icon to display before the title
   */
  icon?: React.ReactNode;

  /**
   * If true, shows an Edit button in the header
   */
  editable?: boolean;

  /**
   * Callback when Edit button is clicked
   */
  onEdit?: () => void;

  /**
   * Completion percentage (0-100) - displays a badge in the header
   */
  completionPercent?: number;

  /**
   * Additional CSS classes for the container
   */
  className?: string;

  /**
   * Content of the card
   */
  children: React.ReactNode;

  /**
   * Optional action buttons in the header
   */
  actions?: React.ReactNode;
}

/**
 * InfoCard Component
 *
 * A reusable card component for displaying information sections
 * with optional edit functionality and completion tracking.
 *
 * @example
 * ```tsx
 * <InfoCard
 *   title="Customer Information"
 *   icon={<UserIcon />}
 *   editable
 *   onEdit={() => setEditMode(true)}
 *   completionPercent={85}
 * >
 *   <InfoRow label="Name" value="John Doe" />
 *   <InfoRow label="Email" value="john@example.com" />
 * </InfoCard>
 * ```
 */
export function InfoCard({
  title,
  icon,
  editable = false,
  onEdit,
  children,
  completionPercent,
  className,
  actions,
  ...rest
}: InfoCardProps) {
  // Determine badge color based on completion percentage
  const getBadgeColor = (percent: number) => {
    if (percent === 100) {
      return 'bg-green-100 text-green-800 border-green-200';
    } else if (percent >= 70) {
      return 'bg-yellow-100 text-yellow-800 border-yellow-200';
    } else if (percent > 0) {
      return 'bg-orange-100 text-orange-800 border-orange-200';
    } else {
      return 'bg-gray-100 text-gray-600 border-gray-200';
    }
  };

  return (
    <div
      className={clsx(
        'rounded-lg border border-gray-200 bg-white shadow-sm transition-shadow hover:shadow-md',
        className
      )}
      {...rest}
    >
      {/* Header */}
      <div className="border-b border-gray-200 bg-gray-50 px-4 py-3 sm:px-6">
        <div className="flex items-center justify-between">
          {/* Title Section */}
          <div className="flex items-center gap-2">
            {icon && (
              <span className="flex-shrink-0 text-gray-500" aria-hidden="true">
                {icon}
              </span>
            )}
            <h3 className="text-base font-semibold text-gray-900 sm:text-lg">
              {title}
            </h3>
          </div>

          {/* Actions Section */}
          <div className="flex items-center gap-3">
            {/* Completion Badge */}
            {completionPercent !== undefined && (
              <span
                className={clsx(
                  'inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold transition-colors',
                  getBadgeColor(completionPercent)
                )}
                aria-label={`${completionPercent}% complete`}
              >
                {completionPercent === 100 ? (
                  <>
                    <svg
                      className="mr-1 h-3 w-3"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                      aria-hidden="true"
                    >
                      <path
                        fillRule="evenodd"
                        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                        clipRule="evenodd"
                      />
                    </svg>
                    Complete
                  </>
                ) : (
                  `${completionPercent}%`
                )}
              </span>
            )}

            {/* Custom Actions */}
            {actions}

            {/* Edit Button */}
            {editable && onEdit && (
              <button
                onClick={onEdit}
                className="text-sm font-medium text-blue-600 transition-colors hover:text-blue-800 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                aria-label={`Edit ${title}`}
              >
                Edit
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="px-4 py-4 sm:px-6">{children}</div>
    </div>
  );
}

export default InfoCard;
